import {
  TeContainerSlim,
  TeLabel,
  TeSearch,
  TeSearchSpotlight,
  TeSpotlightSlot,
  TeText,
} from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';
import { useEffect, useImperativeHandle, useRef } from 'react';

import { useSuggestAddress } from '../pages/RequestBusinessPartner/businessPartnerRequest.mutations';
import { AddressSuggestionItem } from '../api/generated/pm';
import { useSearchCompanyState } from '../hooks/useSearchCompany';

interface SearchCompanyProps {
  countryCode: string;
  onSelectCompany: (company: AddressSuggestionItem | string) => void;
  onFocus?: (e: FocusEvent) => void;
  onBlur?: (e: FocusEvent) => void;
  defaultCompanyName?: string;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  popkey?: string;
}

export interface SearchCompanyRef {
  resetSearch: () => void;
}

type SearchCompanyComponentProps = SearchCompanyProps & { ref?: React.Ref<SearchCompanyRef> };

function formatSuggestionAddress({
  addressLineOne,
  addressLineTwo,
  zipPostalCode,
  city,
  stateProvinceRegion,
  country,
}: AddressSuggestionItem): string {
  const street = addressLineOne ?? addressLineTwo ?? '';
  const locality = [zipPostalCode, city, stateProvinceRegion].filter(Boolean).join(' ');
  return [street, locality, country].filter(Boolean).join(', ');
}

const SearchCompany = ({
  ref,
  countryCode,
  onSelectCompany,
  onFocus,
  onBlur,
  defaultCompanyName,
  label,
  placeholder,
  popkey,
  disabled,
}: SearchCompanyComponentProps) => {
  const {
    showResultsInSpotlight,
    debouncedSearchValue,
    searchQuery,
    setSearchQuery,
    setShowResultsInSpotlight,
    companyName,
    setCompanyName,
  } = useSearchCompanyState({ defaultCompanyName });

  const { mutate: suggestAddress, data } = useSuggestAddress({ retry: false });
  const { t } = useTranslation();

  // Prevent the reset from triggering the suggestions
  const isResettingRef = useRef(false);

  useImperativeHandle(ref, () => ({
    resetSearch() {
      isResettingRef.current = true;

      setSearchQuery('');
      setCompanyName('');
      setShowResultsInSpotlight(false);
    },
  }));

  useEffect(() => {
    if (isResettingRef.current) {
      isResettingRef.current = false;
      return;
    }

    if (!debouncedSearchValue || companyName) return;

    setShowResultsInSpotlight(true);
    suggestAddress({
      name: debouncedSearchValue,
      country: countryCode,
    });
  }, [debouncedSearchValue, countryCode, companyName, suggestAddress, setShowResultsInSpotlight]);

  return (
    <>
      {label && <TeLabel text={label} />}
      <TeSearch
        size="s"
        name="name"
        grow
        placeholder={placeholder ?? t('companyNamePlaceholder', { ns: 'common' })}
        value={companyName ?? searchQuery}
        techange={(e) => {
          setSearchQuery(e.target.value);
        }}
        teclear={() => setSearchQuery('')}
        teblur={(e) => {
          onSelectCompany(companyName ?? searchQuery);
          onBlur?.(e);
        }}
        disabled={disabled}
        tefocus={(e) => onFocus?.(e)}
        popkey={popkey}
      >
        {showResultsInSpotlight && (
          <TeSearchSpotlight
            style={{ maxHeight: '300px' }}
            teclose={() => setShowResultsInSpotlight(false)}
          >
            {data?.items?.length === 0 ? (
              <TeSpotlightSlot key="no-results">
                <TeContainerSlim padding="m" fill grow justifyContent="center">
                  <TeText color="text.secondary" alignment="center">
                    {t('noResultsSearchCompany')}
                  </TeText>
                </TeContainerSlim>
              </TeSpotlightSlot>
            ) : (
              data?.items?.map((item) => {
                const address = formatSuggestionAddress(item);
                return (
                  <TeSpotlightSlot
                    key={`${address}-${item.name}`}
                    teclick={() => {
                      setCompanyName(item.name ?? '');
                      onSelectCompany(item);
                      setShowResultsInSpotlight(false);
                    }}
                  >
                    <TeContainerSlim column fill justifyContent="center" gap="xs2" padding="xs">
                      <TeText weight="bold">{item.name}</TeText>
                      <TeText color="text.secondary" size={12}>
                        {address}
                      </TeText>
                    </TeContainerSlim>
                  </TeSpotlightSlot>
                );
              })
            )}
          </TeSearchSpotlight>
        )}
      </TeSearch>
    </>
  );
};

export default SearchCompany;
